<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import AcceptTerms from '@/Components/AcceptTerms.vue';
import ExtraProducts from '@/Components/ExtraProducts.vue';
import { Head, router } from '@inertiajs/vue3';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
import { trans as $t } from 'laravel-vue-i18n';
</script>

<script>
const bootstrap = typeof window !== `undefined` && import('bootstrap');

export default {

    props: {
        bounds: {
            type: Object
        },
        countries: {
            type: Array
        },
        currencies: {
            type: Array
        },
        currency: {
            type: String,
            required: true
        },
        errors: {
            type: Object
        },
        orderParams: {
            type: Object
        },
        paymentError: {
            type: String
        },
        price: {
            type: Object
        },
        productCategories: {
            type: Object
        },
        redirectUrl: {
            type: String
        },
        user: {
            type: Object
        }
    },

    data() {
        return {
            ajaxRequest: false,
            hasUserAccount: true,
            orderData: {
                currency: '',
                name: '',
                email: '',
                username: '',
                password: '',
                passwordRepeat: '',
                phone: '',
                extraProducts: [],
                invoiceData: {
                    privatePerson: false,
                    name: '',
                    country: '',
                    postcode: '',
                    city: '',
                    address: '',
                    vatNr: '',
                },
                termsAccepted: false,
                totalPrice: {
                    HUF: 0,
                    EUR: 0,
                    details: null,
                },
                paymentMethod: 'card'
            },
            steps: {
                extraProducts: false,
                personalDetails: false,
                login: false,
                invoice: false
            },
            stepNrs: {
                extraProducts: 1,
                personalDetails: 2,
                login: 3,
                invoice: 4
            },
        }
    },

    methods: {

        toggleMobileSidebar() {
            this.$refs.bookingSidebarMobile.classList.toggle('expanded');
        },

        getExtraProduct(productId) {
            let product;
            for (let i = 0; i < this.productCategories.length; i++) {
                for (let j = 0; j < this.productCategories[i].products.length; j++) {
                    product = this.productCategories[i].products[j];
                    if (product.id == productId) {
                        return product;
                    }
                }
            }
        },

        calculatePrice() {

            if (!this.orderData.extraProducts.length) {
                this.orderData.totalPrice = {
                    HUF: 0,
                    EUR: 0,
                    details: null
                }
                return;
            }

            router.post(route('calculateServicePrice'), this.orderData, {
                preserveState: true,
                preserveScroll: true,
                only: ['price', 'errors'],
                onStart: visit => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {
                    if (page.props?.price) {
                        this.orderData.totalPrice = page.props.price;
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                    this.orderData.totalPrice = {
                        HUF: 0,
                        EUR: 0,
                        details: null
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });
        },

        getTotalPrice(currency) {
            return new Intl.NumberFormat().format(currency == 'HUF' ? this.orderData.totalPrice.HUF : this.orderData.totalPrice.EUR) + ' ' + currency;
        },

        getCurrencyPrice(priceHuf, priceEur) {
            return new Intl.NumberFormat().format(this.orderData.currency == 'HUF' ? priceHuf : priceEur) + ' ' + this.orderData.currency;
        },

        // Open extra products tab
        jumpToExtraProducts() {

            this.steps.personalDetails = true;

            bootstrap?.then(({ Collapse }) => {
                Collapse.getOrCreateInstance('#collapseExtraProducts').show();
            });

        },

        // Open personal details tab
        jumpToPersonalDetails() {

            this.ajaxRequest = true;
            router.post(route('checkServiceStep', { step: 'extraProducts' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    this.gtagExtraProducts();

                    this.steps.extraProducts = true;

                    bootstrap?.then(({ Collapse }) => {
                        Collapse.getOrCreateInstance('#collapsePersonal').show();
                    });

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        jumpToLogin() {

            this.errors.username = '';
            this.errors.password = '';

            this.ajaxRequest = true;
            router.post(route('checkServiceStep', { step: 'personalDetails' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    // this.gtagPersonalDetails();

                    this.steps.personalDetails = true;

                    if (this.user) {

                        this.steps.login = true;

                        this.jumpToInvoice();

                    } else {
                        bootstrap?.then(({ Collapse }) => {
                            Collapse.getOrCreateInstance('#collapseLogin').show();
                        });
                    }

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        jumpToInvoice() {

            // Already logged in, do nothing!
            if (this.user) {

                // GA4
                this.gtagBeginCheckout();

                // this.steps.login = true;
                bootstrap?.then(({ Collapse }) => {
                    Collapse.getOrCreateInstance('#collapseInvoice').show();
                });
                return;
            }

            if (this.hasUserAccount) {
                // Login
                router.post(route('authenticate'), {
                    username: this.orderData.username,
                    password: this.orderData.password,
                    remember: true,
                    redirect: false
                }, {
                    only: ['user', 'errors'],
                    preserveState: true,
                    preserveScroll: true,
                    onStart: visit => {
                        this.ajaxRequest = true;
                    },
                    onSuccess: page => {

                        // console.log('Login success!');

                        // Recalculate price
                        this.calculatePrice();

                        // GA4
                        this.gtagBeginCheckout();

                        this.steps.login = true;

                        // Load invoice details from user account
                        if (this.user.invoiceData?.name) {
                            this.orderData.invoiceData = this.user.invoiceData;
                        }

                        this.calculateSteps();

                        if (this.stepNrs.invoice) {
                            bootstrap?.then(({ Collapse }) => {
                                Collapse.getOrCreateInstance('#collapseInvoice').show();
                            });
                        }

                    },
                    onError: errors => {
                        if (errors.alert) {
                            Swal.fire({
                                icon: 'error',
                                text: errors.alert,
                            });
                        } else {
                            this.$utils.scrollToFirstError();
                        }
                    },
                    onFinish: visit => {
                        this.ajaxRequest = false;
                    },
                });
            } else {
                // Register
                router.post(route('register'), {
                    name: this.orderData.name,
                    email: this.orderData.email,
                    phone: this.orderData.phone,
                    username: this.orderData.username,
                    password: this.orderData.password,
                    passwordRepeat: this.orderData.passwordRepeat,
                    remember: true,
                    redirect: false
                }, {
                    only: ['user', 'errors'],
                    preserveState: true,
                    preserveScroll: true,
                    onStart: visit => {
                        this.ajaxRequest = true;
                    },
                    onSuccess: page => {

                        // console.log('Registration success!');

                        // Recalculate price
                        this.calculatePrice();

                        // GA4
                        this.gtagBeginCheckout();

                        this.steps.login = true;

                        bootstrap?.then(({ Collapse }) => {
                            Collapse.getOrCreateInstance('#collapseInvoice').show();
                        });
                    },
                    onError: errors => {
                        if (errors.alert) {
                            Swal.fire({
                                icon: 'error',
                                text: errors.alert,
                            });
                        } else {
                            this.$utils.scrollToFirstError();
                        }
                    },
                    onFinish: visit => {
                        this.ajaxRequest = false;
                    },
                });
            }
        },

        checkInvoiceDetails(method) {

            this.orderData.paymentMethod = method;

            if (this.steps.invoice) {
                if (method == 'card') {
                    this.startPayment();
                } else {
                    // this.savePayment();
                }
                return;
            }

            this.ajaxRequest = true;
            router.post(route('checkServiceStep', { step: 'invoiceDetails' }), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    this.steps.invoice = true;

                    if (method == 'card') {
                        this.startPayment();
                    } else {
                        // this.savePayment();
                    }

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        startPayment() {

            this.ajaxRequest = true;
            router.post(route('startServicePayment'), this.orderData, {
                only: ['redirectUrl', 'errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {

                    // GA4
                    this.gtagPaymentInfo();

                    // Wait a bit before redirection
                    setTimeout(() => {
                        window.location = page.props.redirectUrl;
                    }, 250);

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
                onFinish: visit => {
                    setTimeout(() => {
                        this.ajaxRequest = false;
                    }, 500);
                },
            });

        },

        // Show password reminder modal
        showReminderModal() {

            bootstrap?.then(({ Modal }) => {
                Modal.getOrCreateInstance('#reminderModal').show();
            });

        },

        // Send password reminder e-mail
        sendReminderMail() {

            this.ajaxRequest = true;
            router.post(route('doPasswordReminder'), {
                reminderusername: this.$refs.reminderUsername.value
            }, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                // preserveScroll: (page) => Object.keys(page.props.errors).length,
                preserveScroll: true,
                onSuccess: page => {

                    bootstrap?.then(({ Modal }) => {
                        Modal.getOrCreateInstance('#reminderModal').hide();
                    });

                    Swal.fire({
                        icon: 'success',
                        text: $t('A jelszó visszaállításához szükséges e-mailt elküldtük Önnek!'),
                    });
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } /*else {
                        this.$utils.scrollToFirstError();
                    }*/
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        // GA4 helper: get items
        gtagItems() {

            let items = [];

            let product;
            for (let item of this.orderData.extraProducts) {
                product = this.getExtraProduct(item.id);
                if (product) {
                    items.push({
                        'item_name': product.name,
                        'item_id': product.name,
                        'price': (this.orderData.currency == 'HUF' ? product.price.HUF : product.price.EUR),
                        'currency': this.orderData.currency,
                        'item_category': 'Extra product',
                        'quantity': item.quantity,
                        // 'transfer_type': (this.orderData.tripType == 'return' ? 'Return' : 'One-way') + ' transfer',
                        // 'transfer_direction': this.orderData.transferType == 'AC' ? 'Airport to City' : 'City to Airport',
                        // 'adult_passangers_number': this.orderData.adults,
                        // 'passangers_underage2_number': this.orderData.children,
                        // 'flight_arriving_date': flightArrivalDate,
                        // 'flight_departure_date': flightDepartureDate,
                    });
                }
            }

            return items;

        },

        // GA4: add_to_cart event when jumping from extra products to login / invoice details
        gtagExtraProducts() {

            if (!this.orderData.extraProducts.length) {
                return;
            }

            dataLayer.push({
                'event': 'add_to_cart',
                'ecommerce': {
                    'items': this.gtagItems()
                }
            });

        },

        // GA4: begin_checkout event when arriving to invoice details
        gtagBeginCheckout() {

            dataLayer.push({
                'event': 'begin_checkout',
                'ecommerce': {
                    'items': this.gtagItems()
                }
            });

        },

        // GA4: add_payment_info when
        gtagPaymentInfo() {

            dataLayer.push({
                'event': 'add_payment_info',
                'ecommerce': {
                    'first_name': this.orderData.name,
                    'last_name': '',
                    'email': this.orderData.email,
                    'phone': this.orderData.phone,
                    'postcode': this.orderData.invoiceData.postcode,
                    'country_code': this.orderData.invoiceData.country,
                    'city': this.orderData.invoiceData.city,
                    'street': this.orderData.invoiceData.address,
                    'payment_type': 'Credit card',
                    'items': this.gtagItems(true, true)
                }
            });

        },

        cityFromPostcode() {
            if (!this.orderData.invoiceData.postcode || this.orderData.invoiceData.city) {
                return;
            }

            const that = this;

            axios.get(route('cityFromPostcode'), {
                params: {
                    postcode: that.orderData.invoiceData.postcode
                }
            }).then(function (response) {
                if (response.data?.city) {
                    that.orderData.invoiceData.city = response.data.city;
                }
            });
        },

        postcodeFromCity() {
            if (!this.orderData.invoiceData.city || this.orderData.invoiceData.postcode) {
                return;
            }

            const that = this;

            axios.get(route('postcodeFromCity'), {
                params: {
                    city: that.orderData.invoiceData.city
                }
            }).then(function (response) {
                if (response.data?.postcode) {
                    that.orderData.invoiceData.postcode = response.data.postcode;
                }
            });
        },

        fillUserFields() {

            if (!this.user) {
                return;
            }

            this.steps.login = true;

            // Fill name and email
            this.orderData.name = this.user.name;
            this.orderData.email = this.user.email;
            this.orderData.phone = this.user.phone;

            // Fill invoice details
            if (this.user.invoiceData?.name) {
                // console.log(this.user.invoiceData);
                this.orderData.invoiceData = this.user.invoiceData;
            }

        },

        // Recalculate step numbers
        calculateSteps() {

            let lastStep = 0;

            this.stepNrs.extraProducts = ++lastStep;

            this.stepNrs.personalDetails = ++lastStep;

            if (this.user) {
                this.stepNrs.login = 0;
            } else {
                this.stepNrs.login = ++lastStep;
            }

            this.stepNrs.invoice = ++lastStep;

        },

    },

    watch: {

        hasUserAccount: {
            handler(newValue, oldValue) {
                // Reset error messages on login / register tab switch
                this.errors.username = '';
                this.errors.password = '';
            },
        },

        'orderData.extraProducts': {
            handler(newValue, oldValue) {
                // console.log('Extra products changed, calculate price!');
                this.calculatePrice();
            },
            deep: true
        },

        'orderData.email': {
            handler(newValue, oldValue) {
                // Fill username on email change
                this.orderData.username = this.orderData.email;
            },
        },

    },

    beforeMount() {

        // Fill order data with optional order parameters
        if (this.orderParams) {
            this.orderData = { ...this.orderData, ...this.orderParams };
        }

        // Fill user fields
        this.fillUserFields();

    },

    mounted() {

        if (this.orderParams) {
            this.orderData = { ...this.orderData, ...this.orderParams };
        }

        this.fillUserFields();

        if (this.price) {
            this.orderData.totalPrice = this.price;
        }

        this.orderData.currency = this.currency;

        if (this.paymentError) {
            this.steps.personalDetails = true;
            this.steps.extraProducts = true;
            this.steps.login = true;
            this.steps.invoice = true;
        }

        // Scroll to accordion item on step change
        const accordion = document.getElementById('bookingAccordion');
        const accordionItems = accordion.querySelectorAll('.accordion-item');
        accordionItems.forEach(function (elem) {
            elem.addEventListener('shown.bs.collapse', event => {
                if (event.target) {
                    const headerOffset = event.target.getBoundingClientRect().top;
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetOffset = headerOffset + scrollTop - 150;

                    window.scrollTo({
                        top: targetOffset,
                        behavior: 'smooth'
                    });
                }
            });
        });

        this.calculateSteps();

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'services',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }

}

</script>

<style>
.collapsing {
    -webkit-transition: none;
    transition: none;
}

.input-group .input-group-append .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.modal-open {
    overflow: auto;
    padding-right: 0 !important;
}
</style>

<template>

    <Head :title="$t('Extra szolgáltatások')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('Extra szolgáltatások') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!--<div class="header-placeholder"></div>-->

        <div class="subpage-content">

            <div class="container subpage-container">
                <div class="row">

                    <aside class="booking-sidebar col-12 col-md-4 col-lg-3 order-md-1 mb-3 mb-md-0 d-none d-md-block">
                        <div class="card text-bg-secondary sticky-top">
                            <div class="card-body">
                                <div class="cart-icon">
                                    <i class="far fa-shopping-cart"></i>
                                </div>
                                <div class="text-center mb-2">
                                    <img src="../../images/logo.png" alt="miniBUD" width="133" height="32">
                                </div>
                                <div class="cart-headline">{{ $t('Foglalás összesítő') }}</div>
                                <div class="row form-row align-items-center">
                                    <div class="col-6 text-uppercase text-bold text-left">{{ $t('Pénznem') }}</div>
                                    <div class="col-6">
                                        <ul class="booking-currency-list">
                                            <li v-for="c in currencies" class="booking-currency-list-item" @click="orderData.currency = c" :class="{ 'current': orderData.currency == c }">
                                                {{ c }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="cart-steps">
                                    <div class="cart-step">
                                        <ul class="step-list">
                                            <li class="step-list-item py-3" v-if="orderData.extraProducts.length">
                                                <span>{{ $t('Extra szolgáltatások') }}</span>
                                                <p class="m-0" v-for="product in orderData.extraProducts">{{ product.quantity }}x {{ getExtraProduct(product.id)?.name }}</p>
                                            </li>
                                            <li class="step-list-item py-3" v-else>
                                                <p class="m-0 text-center">{{ $t('Nincs kiválasztva termék!') }}</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <template v-if="orderData.totalPrice.HUF">
                                    <div class="cart-separator"></div>
                                    <div class="cart-headline">{{ $t('Összesen') }}</div>
                                    <div class="cart-total">{{ getTotalPrice(orderData.currency) }}</div>
                                </template>
                            </div>
                        </div>
                    </aside>

                    <main class="main col-12 col-md-8 col-lg-9 order-md-2 mb-3 mb-md-0">
                        <section>
                            <form autocomplete="off">

                                <div class="alert alert-danger d-flex align-items-center" role="alert" v-if="paymentError">
                                    <i class="fas fa-exclamation-circle me-2" role="img" aria-label="Danger:"></i>
                                    <div><strong>{{ $t('Sikertelen tranzakció!') }}</strong> {{ paymentError }}</div>
                                </div>

                                <div class="accordion" id="bookingAccordion">

                                    <!-- Extra products -->
                                    <div class="accordion-item" v-if="stepNrs.extraProducts">
                                        <div class="accordion-header">
                                            <button class="accordion-button" :class="{ 'collapsed': paymentError }" type="button" data-bs-toggle="collapse" data-bs-target="#collapseExtraProducts" aria-controls="collapseExtraProducts" :aria-expanded="paymentError ? false : true">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.extraProducts, 'text-success fa-check-circle': steps.extraProducts }"></i>{{
                                                        stepNrs.extraProducts }}.
                                                    {{ $t('Extra szolgáltatások') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseExtraProducts" class="accordion-collapse collapse" :class="{ 'show': !paymentError }" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <ExtraProducts :currency="orderData.currency" :productCategories="productCategories" :modelValue="orderData.extraProducts" @update:modelValue="newValue => orderData.extraProducts = newValue" />
                                                <div v-if="errors.extraProducts" class="alert alert-danger fw-bold">{{ errors.extraProducts }}</div>
                                                <button type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToPersonalDetails">{{
                                                    $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Extra products -->

                                    <!-- Personal details -->
                                    <div class="accordion-item" v-if="stepNrs.personalDetails">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePersonal" aria-controls="collapsePersonal" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.extraProducts }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.personalDetails, 'text-success fa-check-circle': steps.personalDetails }"></i>{{
                                                        stepNrs.personalDetails }}.
                                                    {{ $t('Személyes adatok') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapsePersonal" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <div class="row">
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('Név')" v-model="orderData.name" maxlength="150" autocomplete="name">
                                                            <label for="name">{{ $t('Név') }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" v-model="orderData.email" maxlength="150" :readonly="user?.isHotel" autocomplete="email">
                                                            <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="$t('Mobiltelefonszám')" v-model="orderData.phone" :readonly="user?.isHotel" />
                                                            <label for="phone">{{ $t('Mobiltelefonszám') }}<span class="text-danger">*</span></label>
                                                            <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <hr />

                                                <button type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToLogin">{{
                                                    $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Personal details -->

                                    <!-- Login or registration -->
                                    <div class="accordion-item" v-if="stepNrs.login">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseLogin" aria-controls="collapseLogin" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.extraProducts }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.login, 'text-success fa-check-circle': steps.login }"></i>{{
                                                        stepNrs.login }}. {{ $t('Felhasználói fiók') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseLogin" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">

                                                <div v-if="!user">
                                                    <div class="col-12">
                                                        <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Bejelentkezés')">
                                                            <input type="radio" class="btn-check" name="hasUserAccount" id="acc-yes" autocomplete="off" :value="true" v-model="hasUserAccount">
                                                            <label class="btn btn-outline-secondary w-100" for="acc-yes"><i class="fal fa-user-check me-1"></i>
                                                                {{ $t('Már van miniBUD fiókom') }}</label>
                                                            <input type="radio" class="btn-check" name="hasUserAccount" id="acc-no" autocomplete="off" :value="false" v-model="hasUserAccount">
                                                            <label class="btn btn-outline-secondary w-100" for="acc-no"><i class="fal fa-user-plus me-1"></i> {{
                                                                $t('Még nincs miniBUD fiókom') }}</label>
                                                        </div>
                                                    </div>
                                                    <div v-if="hasUserAccount" class="col-12">
                                                        <p class="fs-6" v-html="$t('Ha már van miniBUD fiókja, kérjük, felhasználói nevével és jelszavával jelentkezzen be, majd kattintson a Következő gombra!')">
                                                        </p>
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" id="username" name="username" :placeholder="$t('Felhasználói név / e-mail cím')" :class="{ 'is-invalid': errors.username }" v-model="orderData.username" maxlength="150" autocomplete="username">
                                                            <label for="username">{{ $t('Felhasználói név / e-mail cím') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="loginPassword" :placeholder="$t('Jelszó')" :class="{ 'is-invalid': errors.password }" v-model="orderData.password" maxlength="50" autocomplete="current-password">
                                                            <label for="loginPassword">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                        </div>
                                                        <div class="mb-3 text-center">
                                                            <a href="javascript:;" class="text-secondary" @click="showReminderModal">{{ $t('Elfelejtettem a jelszavamat') }}</a>
                                                        </div>
                                                    </div>
                                                    <div v-if="!hasUserAccount" class="col-12">
                                                        <p class="fs-6" v-html="$t('Ha még nincs miniBUD fiókja, kérjük, adja meg a kívánt felhasználói nevet és jelszót, majd kattintson a Következő gombra!')">
                                                        </p>
                                                        <div class="form-floating mb-3">
                                                            <input type="email" class="form-control" id="regUsername" name="username" :placeholder="$t('Felhasználói név')" :class="{ 'is-invalid': errors.username }" v-model="orderData.username" maxlength="150" autocomplete="username">
                                                            <label for="regUsername">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="regPassword" name="password" :placeholder="$t('Jelszó')" :class="{ 'is-invalid': errors.password }" v-model="orderData.password" maxlength="50" autocomplete="new-password">
                                                            <label for="regPassword">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                        </div>
                                                        <div class="form-floating mb-3">
                                                            <input type="password" class="form-control" id="loginPasswordRepeat" :placeholder="$t('Jelszó ismét')" :class="{ 'is-invalid': errors.passwordRepeat }" v-model="orderData.passwordRepeat" maxlength="50" autocomplete="new-password">
                                                            <label for="loginPasswordRepeat">{{ $t('Jelszó ismét') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors.passwordRepeat" class="invalid-feedback">{{ errors.passwordRepeat }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-if="user">
                                                    <p class="mb-3">{{ $t('Bejelentkezve, mint :name', { name: user.name }) }}</p>
                                                    <p class="mb-3"><a :href="route('logout')">{{ $t('Kijelentkezés') }}</a></p>
                                                </div>
                                                <button v-if="stepNrs.invoice" type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="jumpToInvoice">{{ $t('Következő') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Login or registration -->

                                    <!-- Invoice details -->
                                    <div class="accordion-item" v-if="stepNrs.invoice">
                                        <div class="accordion-header">
                                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInvoice" aria-controls="collapseInvoice" aria-expanded="false" :class="{ 'pe-none text-muted': !steps.login || !steps.extraProducts }">
                                                <h2 class="my-3">
                                                    <i class="fas me-2" :class="{ 'text-muted fa-question-circle': !steps.invoice, 'text-success fa-check-circle': steps.invoice }"></i>{{
                                                        stepNrs.invoice }}. {{ $t('Számlázási adatok') }}
                                                </h2>
                                            </button>
                                        </div>
                                        <div id="collapseInvoice" class="accordion-collapse collapse" data-bs-parent="#bookingAccordion">
                                            <div class="accordion-body">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <p class="fw-bold my-3">{{ $t('Vásárló') }}<span class="text-danger">*</span></p>
                                                        <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Vásárló')">
                                                            <input type="radio" class="btn-check" name="buyerType" id="company" autocomplete="off" :value="false" v-model="orderData.invoiceData.privatePerson">
                                                            <label class="btn btn-outline-secondary w-100" for="company"><i class="fal fa-building me-1"></i>
                                                                {{ $t('Cég') }}</label>
                                                            <input type="radio" class="btn-check" name="buyerType" id="private" autocomplete="off" :value="true" v-model="orderData.invoiceData.privatePerson">
                                                            <label class="btn btn-outline-secondary w-100" for="private"><i class="fal fa-user me-1"></i> {{
                                                                $t('Magánszemély') }}</label>
                                                            <div v-if="errors['invoiceData.privatePerson']" class="text-danger">{{ errors['invoiceData.privatePerson'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.name'] }" id="invName" :placeholder="$t(orderData.invoiceData.privatePerson ? 'Név' : 'Cégnév')" maxlength="150" v-model="orderData.invoiceData.name" />
                                                            <label for="invName">
                                                                <span v-if="orderData.invoiceData.privatePerson">{{ $t('Név') }}</span><span v-else>{{ $t('Cégnév') }}</span>
                                                                <span class="text-danger">*</span>
                                                            </label>
                                                            <div v-if="errors['invoiceData.name']" class="invalid-feedback">{{ errors['invoiceData.name'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-floating mb-3">
                                                            <select class="form-select" :class="{ 'is-invalid': errors['invoiceData.country'] }" name="invCountry" id="invCountry" v-model="orderData.invoiceData.country">
                                                                <option v-for="c in countries" :value="$page.props.locale == 'hu' ? c.name_hu : c.name_en">{{
                                                                    $page.props.locale == 'hu' ? c.name_hu : c.name_en }}</option>
                                                            </select>
                                                            <label for="invCountry">{{ $t('Ország') }}</label>
                                                            <div v-if="errors['invoiceData.country']" class="invalid-feedback">{{ errors['invoiceData.country'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-4">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.postcode'] }" id="invPostcode" maxlength="10" :placeholder="$t('Irányítószám')" v-model="orderData.invoiceData.postcode" @change="cityFromPostcode" />
                                                            <label for="invPostcode">{{ $t('Irányítószám') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.postcode']" class="invalid-feedback">{{ errors['invoiceData.postcode'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-8">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.city'] }" id="invCity" maxlength="50" :placeholder="$t('Település')" v-model="orderData.invoiceData.city" @change="postcodeFromCity" />
                                                            <label for="invCity">{{ $t('Település') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.city']" class="invalid-feedback">{{ errors['invoiceData.city'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div :class="[orderData.invoiceData.privatePerson ? 'col-12' : 'col-md-8']">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.address'] }" id="invAddress" maxlength="150" :placeholder="$t('Cím (utca, házszám)')" v-model="orderData.invoiceData.address" />
                                                            <label for="invAddress">{{ $t('Cím (utca, házszám)') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.address']" class="invalid-feedback">{{ errors['invoiceData.address'] }}</div>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-4" v-if="!orderData.invoiceData.privatePerson">
                                                        <div class="form-floating mb-3">
                                                            <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.vatNr'] }" id="invVatNr" maxlength="20" :placeholder="$t('Adószám')" v-model="orderData.invoiceData.vatNr" />
                                                            <label for="invVatNr">{{ $t('Adószám') }}<span class="text-danger">*</span></label>
                                                            <div v-if="errors['invoiceData.vatNr']" class="invalid-feedback">{{ errors['invoiceData.vatNr'] }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- ./Invoice details -->

                                </div>

                                <hr />

                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-check mb-3">
                                            <AcceptTerms class="form-check-input" :class="{ 'is-invalid': errors.termsAccepted }" id="acceptTerms" v-model="orderData.termsAccepted" />
                                            <div v-if="errors.termsAccepted" class="invalid-feedback">{{ errors.termsAccepted }}</div>
                                        </div>
                                        <button type="button" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="checkInvoiceDetails('card')">
                                            {{ $t('Tovább a fizetéshez') }}
                                        </button>
                                    </div>
                                </div>

                            </form>

                        </section>
                    </main>

                </div>
            </div>

            <aside class="booking-sidebar-mobile d-block d-md-none fixed-bottom" ref="bookingSidebarMobile">
                <div class="card text-bg-secondary rounded-0 border-0 h-100" style="--bs-bg-opacity: .9;">
                    <div class="card-body h-100">
                        <div class="d-flex flex-column h-100">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <button type="button" class="btn btn-link text-white" @click="toggleMobileSidebar" :aria-label="$t('Részletek')"><i class="far fa-expand-alt"></i></button>
                                <div class="btn-group" role="group" :aria-label="$t('Pénznem')">
                                    <template v-for="c in currencies">
                                        <input type="radio" class="btn-check" name="currency" :id="'currency-' + c" autocomplete="off" :checked="orderData.currency == c" @click="orderData.currency = c">
                                        <label class="btn btn-xs btn-outline-primary text-white" :for="'currency-' + c">{{ getTotalPrice(c) }}</label>
                                    </template>
                                </div>
                                <div>&nbsp;</div>
                            </div>
                            <div class="overflow-auto">
                                <div class="cart-steps">
                                    <div class="cart-step">
                                        <ul class="step-list">
                                            <li class="step-list-item py-3" v-if="orderData.extraProducts.length">
                                                <span>{{ $t('Extra szolgáltatások') }}</span>
                                                <p class="m-0" v-for="product in orderData.extraProducts">{{ product.quantity }}x {{ getExtraProduct(product.id)?.name }}</p>
                                            </li>
                                            <li class="step-list-item py-3" v-else>
                                                <span>{{ $t('Nincs kiválasztva termék!') }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

        </div>

        <div id="reminderModal" class="modal fade" tabindex="-1">
            <div class="modal-dialog modal-fullscreen-md-down">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('Elfelejtett jelszó') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-12">
                                <p>{{ $t('Elfelejtette jelszavát? Adja meg a regisztrált felhasználói nevét vagy e-mail címét, és segítünk Önnek!') }}</p>
                            </div>
                            <div class="col-12">
                                <div class="form-floating mb-3">
                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.reminderusername }" id="reminderUsername" :placeholder="$t('Felhasználói név')" ref="reminderUsername">
                                    <label for="reminderUsername">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                    <div v-if="errors.reminderusername" class="invalid-feedback">{{ errors.reminderusername }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('Mégsem') }}</button>
                        <button type="button" class="btn btn-primary" :disabled="ajaxRequest" @click="sendReminderMail">{{ $t('Küldés') }}</button>
                    </div>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
